import React, { useState } from 'react';
import { Dropdown, Grid } from 'semantic-ui-react';

import {
  SelectWrapper,
  ButtonsWrapper,
  DateFieldPrefix,
  DateFieldsWrapper,
  ReportSectionWrapper
} from '../../styles';
import Button from '../../../../components/Button';
import { FieldLabel } from '../../../../styles';
import { iRootDispatch, iRootState } from '../../../../store';
import { connect } from 'react-redux';
import { Props } from './types';
import {
  dangerNotification,
  successNotification
} from '../../../../services/notification';
import CustomDatePicker from '../../../../components/CustomDatePicker';
import AppMaskedInput from '../../../../components/MaskedInput';
import { DateToStringDate } from '../../../../services/date';
import { AvailableBrokersAndEstatesState } from '../../../../store/types/temp-types';
import axiosInstance from '../../../../services/axios-instance';
import { dateFromString } from '../../helpers';
import { isBackOfficeUser } from '../../../../utils/isBackOfficeUser';
import EstateUnitFilter from '../../../../components/EstateUnitFilter';
import {
  buildEstateUnitQueryString,
  resolveEstateIdForUnitFilter
} from '../../../../utils/estateUnitFilter.util';

const dateMaskInput = (
  <AppMaskedInput
    mask={[
      /[0-9]/,
      /[0-9]/,
      '/',
      /[0-9]/,
      /[0-9]/,
      '/',
      /[0-9]/,
      /[0-9]/,
      /[0-9]/,
      /[0-9]/
    ]}
  />
);

const FireInsuranceInvoiceDataReport: React.FC<Props> = ({
  closeSection,
  availableBrokersAndEstates,
  user
}) => {
  const [selectedEstatesValues, setSelectedEstatesValues] = useState<string[]>([]);
  const [estateUnitIdFilter, setEstateUnitIdFilter] = useState('');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [isExportLoading, setIsExportLoading] = useState(false);
  const canSelectEstate = isBackOfficeUser();

  const estateOptions =
    availableBrokersAndEstates.estates?.map(option => ({
      text: option.text,
      value: option.value.id as string
    })) || [];

  const onExportButtonClick = async () => {
    setIsExportLoading(true);

    let formattedStart = '';
    let formattedEnd = '';

    if (start) {
      const [day, month, year] = start.split('/');

      formattedStart = `${year}-${month}-${day}`;
    }

    if (end) {
      const [day, month, year] = end.split('/');

      formattedEnd = `${year}-${month}-${day}`;
    }

    try {
      const response = await axiosInstance.get('/reports/fire-insurance-invoices/xlsx', {
        params: {
          isRv: true,
          start: formattedStart,
          end: formattedEnd,
          estateId: selectedEstatesValues,
          ...(estateUnitIdFilter ? { estateUnitId: estateUnitIdFilter } : {})
        },
        responseType: 'blob'
      });

      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      const url = URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = url;
      a.download = 'Relatório de faturas.xlsx';

      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      URL.revokeObjectURL(url);

      setIsExportLoading(false);

      successNotification('Sucesso!', 'O relatório de faturas foi exportado!');
    } catch (error) {
      setIsExportLoading(false);

      dangerNotification(
        'Oops...',
        'Não foi possível exportar o relatório de faturas, tente novamente mais tarde.'
      );
    }
  };

  const formattedStart = dateFromString(start);
  const formattedEnd = dateFromString(end);

  return (
    <ReportSectionWrapper>
      <Grid>
        <Grid.Row style={{ paddingTop: 0, paddingBottom: 0 }}>
          {canSelectEstate && (
            <Grid.Column width={12}>
              <FieldLabel paddingBottom>Imobiliária(s)</FieldLabel>

              <SelectWrapper style={{ marginBottom: 0 }}>
                <Dropdown
                  id="realEstatesSelect"
                  placeholder="Todas imobiliárias"
                  value={selectedEstatesValues}
                  options={estateOptions || []}
                  onChange={(_, data) => setSelectedEstatesValues(data.value as string[])}
                  style={{ marginBottom: '5px' }}
                  menu={{ style: { maxHeight: '300px', overflowY: 'auto' } }}
                  noResultsMessage={'Carregando...'}
                  clearable
                  fluid
                  multiple
                  search
                  selection
                />
              </SelectWrapper>
            </Grid.Column>
          )}
        </Grid.Row>

        <Grid.Row style={{ paddingTop: 0, paddingBottom: 0 }}>
          <Grid.Column width={8}>
            <EstateUnitFilter
              estateId={resolveEstateIdForUnitFilter(
                selectedEstatesValues,
                availableBrokersAndEstates.activeEstateId ?? undefined
              )}
              value={estateUnitIdFilter}
              onChange={setEstateUnitIdFilter}
            />
          </Grid.Column>
        </Grid.Row>

        <Grid.Row style={{ paddingTop: '0', paddingBottom: '0' }}>
          <Grid.Column width={12}>
            <FieldLabel>Período de vencimento:</FieldLabel>

            <DateFieldsWrapper>
              <div>
                <DateFieldPrefix>De</DateFieldPrefix>

                <CustomDatePicker
                  value={start}
                  onChange={(value: Date) => setStart(DateToStringDate(value))}
                  maxDate={formattedEnd || undefined}
                  customInput={dateMaskInput}
                />
              </div>

              <div>
                <DateFieldPrefix>Até</DateFieldPrefix>

                <CustomDatePicker
                  value={end}
                  onChange={(value: Date) => setEnd(DateToStringDate(value))}
                  minDate={formattedStart || undefined}
                  customInput={dateMaskInput}
                />
              </div>
            </DateFieldsWrapper>
          </Grid.Column>
        </Grid.Row>
      </Grid>

      <ButtonsWrapper>
        <Button
          type="button"
          text="Cancelar"
          disabled={isExportLoading}
          onClick={closeSection}
          light
        />

        <Button
          type="button"
          text="Solicitar relatório"
          disabled={isExportLoading}
          onClick={() => onExportButtonClick()}
        />
      </ButtonsWrapper>
    </ReportSectionWrapper>
  );
};

const mapStateToProps = (state: iRootState) => ({
  availableBrokersAndEstates: state.availableBrokersAndEstates,
  user: state.user
});

const mapDispatch = (dispatch: iRootDispatch) => ({
  updateAvailableBrokersAndEstates: (availableBrokers: AvailableBrokersAndEstatesState) =>
    dispatch.availableBrokersAndEstates.updateAvailableBrokersAndEstates(availableBrokers)
});

export default connect(mapStateToProps, mapDispatch)(FireInsuranceInvoiceDataReport);
